import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Filter,
  Edit, 
  Trash2, 
  Tag,
  FileText,
  MoreVertical
} from 'lucide-react';
import ContextModal from '../components/ContextModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import toast from 'react-hot-toast';

const Context = () => {
  const { id: clientId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [editingContext, setEditingContext] = useState(null);
  const [deletingContext, setDeletingContext] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showDeleteSitemapModal, setShowDeleteSitemapModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, refetch } = useQuery(
    ['context', clientId, currentPage, searchTerm, selectedCategory],
    async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      
      const response = await axios.get(`/context/client/${clientId}?${params}`);
      return response.data;
    },
    { keepPreviousData: true }
  );

  const handleDelete = async (contextId) => {
    try {
      await axios.delete(`/context/${contextId}`);
      toast.success('Context deleted successfully');
      refetch();
      setDeletingContext(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete context');
    }
  };

  const handleDeleteAll = async () => {
    try {
      const response = await axios.delete(`/context/client/${clientId}/all`);
      toast.success(response.data.message);
      refetch();
      setShowDeleteAllModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete all contexts');
    }
  };

  const handleDeleteSitemapContent = async () => {
    try {
      const response = await axios.delete(`/context/client/${clientId}/sitemap`);
      toast.success(response.data.message);
      refetch();
      setShowDeleteSitemapModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete sitemap content');
    }
  };

  const handleEdit = (context) => {
    setEditingContext(context);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingContext(null);
  };

  const handleModalSuccess = () => {
    refetch();
    handleModalClose();
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-300 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  const contexts = data?.contexts || [];
  const categories = data?.categories || [];
  const pagination = data?.pagination || {};

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to={`/clients/${clientId}`}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Client
        </Link>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Context Management</h1>
            <p className="text-gray-600">Manage chatbot knowledge base</p>
          </div>
          <div className="flex space-x-3">
            {contexts.length > 0 && (
              <>
                <button
                  onClick={() => setShowDeleteSitemapModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-white hover:bg-orange-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Sitemap Content
                </button>
                <button
                  onClick={() => setShowDeleteAllModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All
                </button>
              </>
            )}
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Context
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search context..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <select
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Context Grid */}
      {contexts.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {contexts.map((context) => (
              <div
                key={context._id}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow border"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900 truncate flex-1">
                      {context.title}
                    </h3>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={() => handleEdit(context)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeletingContext(context)}
                        className="text-gray-400 hover:text-red-600 p-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mb-3 line-clamp-3">
                    {context.content}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                      {context.category}
                    </span>
                    <div className="flex items-center space-x-2">
                      {context.priority > 0 && (
                        <span className="text-xs text-yellow-600 font-medium">
                          Priority: {context.priority}
                        </span>
                      )}
                      <span className={`w-2 h-2 rounded-full ${
                        context.isActive ? 'bg-green-400' : 'bg-red-400'
                      }`}></span>
                    </div>
                  </div>
                  
                  {context.tags && context.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {context.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                      {context.tags.length > 3 && (
                        <span className="text-xs text-gray-500">
                          +{context.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-8 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing page {pagination.current} of {pagination.pages} 
                ({pagination.total} total items)
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= pagination.pages}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No context found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || selectedCategory !== 'all' 
              ? 'Try adjusting your search or filter criteria' 
              : 'Start by adding your first context item'
            }
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Context
          </button>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ContextModal
          context={editingContext}
          clientId={clientId}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {deletingContext && (
        <DeleteConfirmModal
          title="Delete Context"
          message={`Are you sure you want to delete "${deletingContext.title}"? This action cannot be undone.`}
          onConfirm={() => handleDelete(deletingContext._id)}
          onCancel={() => setDeletingContext(null)}
        />
      )}

      {showDeleteAllModal && (
        <DeleteConfirmModal
          title="Delete All Contexts"
          message={`Are you sure you want to delete ALL ${contexts.length} contexts? This action cannot be undone and will permanently remove all context data for this client.`}
          onConfirm={handleDeleteAll}
          onCancel={() => setShowDeleteAllModal(false)}
          confirmText="Delete All"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}

      {showDeleteSitemapModal && (
        <DeleteConfirmModal
          title="Delete Sitemap-Generated Content"
          message={`Are you sure you want to delete all sitemap-generated contexts and cached sitemap data? This action cannot be undone. Manual contexts will be preserved.`}
          onConfirm={handleDeleteSitemapContent}
          onCancel={() => setShowDeleteSitemapModal(false)}
          confirmText="Delete Sitemap Content"
          confirmButtonClass="bg-orange-600 hover:bg-orange-700"
        />
      )}
    </div>
  );
};

export default Context;